import React from 'react';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { TaskStatusIcon } from 'src/modules/shared/components/molecules/tasks/task-status-icon';
import { useTaskOperations } from 'src/modules/shared/hooks/tasks/use-task-operations';
import { isTaskOverdue } from 'src/modules/shared/utils/tasks/date.util';
import type { Task } from 'src/api/generated/models/Task';
import { Card } from 'src/modules/shared/components/atoms/cards';

/**
 * Calendar-specific task item component - compact version of TaskItem
 */
interface CalendarTaskItemProps {
  task: Task;
  onOpenModal?: () => void;
}

export const CalendarTaskItem: React.FC<CalendarTaskItemProps> = ({
  task,
  onOpenModal,
}) => {
  const {
    handleToggleComplete,
    getTooltipContent,
    isTaskFinished,
    updatingTask,
  } = useTaskOperations();

  const finished = isTaskFinished(task);
  const isOverdue = isTaskOverdue(task);

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    void handleToggleComplete(task);
  };

  const handleTaskClick = (e: React.MouseEvent | undefined) => {
    e?.stopPropagation();
    if (onOpenModal) {
      onOpenModal();
    }
  };

  const getTaskTextClassName = (): string => {
    const baseClasses =
      'font-normal leading-tight truncate text-xs transition-colors';

    if (finished) {
      return `${baseClasses} line-through opacity-70 text-neutral-500`;
    }

    const activeClasses = 'text-primary-500 group-hover:text-primary-600';
    const overdueClasses = isOverdue
      ? 'text-danger-500 font-medium group-hover:text-danger-600'
      : '';

    return `${baseClasses} ${activeClasses} ${overdueClasses}`.trim();
  };

  return (
    <Card
      variant="primary"
      appearance="outline"
      size="xs"
      shadow="none"
      className="p-1 py-0.5 relative cursor-pointer group pr-8"
      onClick={(e) => handleTaskClick(e)}
    >
      <div className="flex items-center gap-1">
        <div className="flex items-center flex-shrink-0">
          <Tooltip content={getTooltipContent(task)} placement="top" size="xs">
            <Button
              onClick={handleStatusClick}
              className="flex-shrink-0 rounded-full opacity-60 group-hover:opacity-100 transition-opacity"
              variant="primary"
              appearance="ghost"
              isIconOnly
              size="xs"
              disabled={updatingTask}
            >
              <TaskStatusIcon task={task} className="h-4 w-4" />
            </Button>
          </Tooltip>
        </div>

        <Tooltip
          content={
            <Text size="small" className="max-w-[300px]">
              {/* TODO: check when implementing i18n */}
              {task.objective || 'Tarea sin título'}
            </Text>
          }
          placement="top"
          size="xs"
        >
          <div className="flex-1 min-w-0 overflow-hidden">
            <Text size="tiny" className={getTaskTextClassName()}>
              {/* TODO: check when implementing i18n */}
              {task.objective || 'Tarea sin título'}
            </Text>
          </div>
        </Tooltip>
      </div>
    </Card>
  );
};
